import React, { RefObject, useEffect, useId, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from './button';
import { BLOCKING_OVERLAY_FADE_MS } from '../../constants/blockingOverlay';

export interface BlockingStatusOverlayAction {
  label: string;
  onClick: () => void;
}

interface BlockingStatusOverlayProps {
  isOpen: boolean;
  title: string;
  subtitle?: string;
  /** One secondary action (e.g. End session). Without it the overlay has no control. */
  action?: BlockingStatusOverlayAction;
}

/**
 * Keeps the overlay mounted for its fade-out after `isOpen` goes false, so it
 * leaves as smoothly as it arrived.
 */
function useMountedThroughFadeOut(isOpen: boolean): boolean {
  const [isRendered, setIsRendered] = useState(isOpen);

  useEffect(() => {
    if (isOpen) {
      setIsRendered(true);
      return undefined;
    }
    const timer = setTimeout(() => setIsRendered(false), BLOCKING_OVERLAY_FADE_MS);
    return () => clearTimeout(timer);
  }, [isOpen]);

  return isOpen || isRendered;
}

/**
 * While open, focus sits on the action (or the overlay itself when there is
 * none). On close it goes back to whatever had it before, unless the user has
 * since moved it somewhere else.
 */
function useFocusWhileOpen(
  isOpen: boolean,
  containerRef: RefObject<HTMLDivElement | null>,
  actionRef: RefObject<HTMLButtonElement | null>
): void {
  useEffect(() => {
    if (!isOpen) return undefined;
    const container = containerRef.current;
    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    (actionRef.current ?? container)?.focus();

    return () => {
      const focused = document.activeElement;
      const isFocusStillOurs = !focused || focused === document.body || !!container?.contains(focused);
      if (isFocusStillOurs && previouslyFocused?.isConnected) previouslyFocused.focus();
    };
  }, [isOpen, containerRef, actionRef]);
}

/**
 * A full-window blocking status, the same overlay as the desktop apps (macOS
 * ConversationView): a dark scrim over everything, a large spinner, a heading
 * and a muted subtitle, fading in and out over 0.25 s. It cannot be dismissed
 * (Escape does nothing); it clears when `isOpen` goes false. Tab stays on its
 * own control while it is open. Modals (z-1300) still sit above it.
 */
export const BlockingStatusOverlay: React.FC<BlockingStatusOverlayProps> = ({ isOpen, title, subtitle, action }) => {
  const isMounted = useMountedThroughFadeOut(isOpen);
  const containerRef = useRef<HTMLDivElement>(null);
  const actionRef = useRef<HTMLButtonElement>(null);
  const titleId = useId();
  const subtitleId = useId();
  useFocusWhileOpen(isOpen, containerRef, actionRef);

  if (!isMounted) return null;

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      // Not dismissible, and nothing underneath may treat this Escape as its own.
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    if (event.key === 'Tab') {
      event.preventDefault();
      (actionRef.current ?? containerRef.current)?.focus();
    }
  };

  const fadeClasses = isOpen ? 'animate-fade-in' : 'pointer-events-none animate-fade-out';

  return (
    <div
      ref={containerRef}
      role="alertdialog"
      aria-modal="true"
      aria-live="assertive"
      aria-labelledby={titleId}
      aria-describedby={subtitle ? subtitleId : undefined}
      aria-hidden={isOpen ? undefined : true}
      inert={isOpen ? undefined : true}
      tabIndex={-1}
      onKeyDown={handleKeyDown}
      className={`fixed inset-0 z-overlay flex items-center justify-center bg-overlay-scrim focus:outline-none ${fadeClasses}`}
    >
      <div className="flex max-w-md flex-col items-center gap-4 p-8 text-center">
        <Loader2
          aria-hidden="true"
          data-testid="blocking-overlay-spinner"
          className="h-10 w-10 animate-spin text-on-overlay"
        />
        <h2 id={titleId} className="heading-4 text-on-overlay">
          {title}
        </h2>
        {subtitle && (
          <p id={subtitleId} className="text-sm leading-body-s text-on-overlay-muted">
            {subtitle}
          </p>
        )}
        {action && (
          <Button ref={actionRef} variant="secondary" size="sm" onClick={action.onClick} className="mt-2">
            {action.label}
          </Button>
        )}
      </div>
    </div>
  );
};

export default BlockingStatusOverlay;
